import { Alert, Button } from 'antd'
import type { LifecycleActionComponentProps } from 'features/libra/lifecycle/lifecycleActionComponents'
import { registerActionComponent } from 'features/libra/lifecycle/lifecycleActionComponents'
import { useGeckoNotifications } from 'features/notifications'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

export const TicketStatusBanner: FC<LifecycleActionComponentProps> = ({
  actions,
  ticket,
  currentStage,
  isTerminal,
}) => {
  const [loadingAction, setLoadingAction] = useState<string | null>(null)
  const { errorNotification } = useGeckoNotifications()
  const mountedRef = useRef(true)
  useEffect(() => {
    // Reset on setup so React 18 strict-mode's setup→cleanup→setup replay
    // doesn't leave the ref stuck at `false` for the remainder of the session.
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const wrappedActions = useMemo(
    () =>
      actions.map((a) => ({
        key: a.transition.to,
        label: a.label,
        handler: async () => {
          setLoadingAction(a.transition.to)
          try {
            await a.execute()
          } catch (err) {
            errorNotification(
              `Failed to ${a.label.toLowerCase()}: ${
                err instanceof Error ? err.message : String(err)
              }`
            )
          } finally {
            if (mountedRef.current) setLoadingAction(null)
          }
        },
      })),
    [actions, errorNotification]
  )

  if (isTerminal) {
    const transitionDate = ticket.terminalTransitionAt
      ? new Date(ticket.terminalTransitionAt).toLocaleDateString()
      : ''
    return (
      <StyledAlert
        type='success'
        showIcon
        message={
          <BannerContent>
            {currentStage.name}
            {ticket.terminalTransitionBy && (
              <TerminalMeta>
                by {ticket.terminalTransitionBy}
                {transitionDate ? ` on ${transitionDate}` : ''}
              </TerminalMeta>
            )}
          </BannerContent>
        }
      />
    )
  }

  if (actions.length === 0) {
    return (
      <StyledAlert
        type='info'
        showIcon
        message={
          <BannerContent>
            <StageDot $color={currentStage.color} />
            {currentStage.name}
          </BannerContent>
        }
      />
    )
  }

  return (
    <StyledAlert
      type='info'
      showIcon
      message={
        <BannerContent>
          <StageDot $color={currentStage.color} />
          {currentStage.name}
        </BannerContent>
      }
      action={
        <BannerActions>
          {wrappedActions.map((wa) => (
            <Button
              key={wa.key}
              size='small'
              type='default'
              loading={loadingAction === wa.key}
              disabled={loadingAction !== null}
              onClick={wa.handler}>
              {wa.label}
            </Button>
          ))}
        </BannerActions>
      }
    />
  )
}

registerActionComponent('TicketStatusBanner', TicketStatusBanner)

const StyledAlert = styled(Alert)`
  width: 100%;
  margin: 0 0 0.75rem;
`

const BannerContent = styled.span`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const BannerActions = styled.div`
  display: flex;
  gap: 0.5rem;
`

const StageDot = styled.span<{ $color?: string }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color ?? '#999'};
  flex-shrink: 0;
`

const TerminalMeta = styled.span`
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-weight: 400;
  margin-left: 0.25rem;
`
